import { runtimeConfig } from "../config/runtimeConfig";

export class RequestTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = "RequestTimeoutError";
  }
}

type RequestDeadlineOptions = {
  timeoutMs?: number;
  signal?: AbortSignal;
};

function getAbortReason(signal: AbortSignal): Error {
  return signal.reason instanceof Error
    ? signal.reason
    : new DOMException("The request was aborted", "AbortError");
}

export async function withRequestDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  options: RequestDeadlineOptions = {},
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? runtimeConfig.apiRequestTimeoutMs;
  const controller = new AbortController();
  const abortFromCaller = () =>
    controller.abort(getAbortReason(options.signal!));

  if (options.signal?.aborted) {
    controller.abort(getAbortReason(options.signal));
  } else {
    options.signal?.addEventListener("abort", abortFromCaller, { once: true });
  }

  const timeoutId = globalThis.setTimeout(() => {
    controller.abort(new RequestTimeoutError(timeoutMs));
  }, timeoutMs);

  const aborted = new Promise<never>((_, reject) => {
    if (controller.signal.aborted) {
      reject(getAbortReason(controller.signal));
      return;
    }
    controller.signal.addEventListener(
      "abort",
      () => reject(getAbortReason(controller.signal)),
      { once: true },
    );
  });

  try {
    return await Promise.race([operation(controller.signal), aborted]);
  } finally {
    globalThis.clearTimeout(timeoutId);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}
